import type { StatusCategory, WorkTypeCode } from "../api/types";

/**
 * Status badge catalogue (UX-01 4.10). Labels and categories come from the API (StatusRef, generated from the
 * SA-02 seed); this table adds only what the seed does not carry: the visual modifier and the SLA-pause words.
 * Every status code in db/seed/workflows must appear here; badges.test.ts diffs the two (BDG-AC1).
 */

export type BadgeModifier = "success" | "negative" | "alert" | "hold" | "waiting";

export interface BadgeSpec {
  modifier?: BadgeModifier;
  /** SR/Incident statuses that pause the SLA clock, and why. */
  slaPause?: "customer" | "internal";
  /** Tooltip for easily confused statuses. */
  hint?: string;
  /** In flight now (Deploying): a static "live" dot, never an animation. */
  live?: boolean;
}

const none: BadgeSpec = {};
const success: BadgeSpec = { modifier: "success" };
const negative: BadgeSpec = { modifier: "negative" };
const hold: BadgeSpec = { modifier: "hold" };
const waiting: BadgeSpec = { modifier: "waiting" };
const alert: BadgeSpec = { modifier: "alert" };

const simplified = { TO_DO: none, IN_PROGRESS: none, DONE: success };

const serviceDesk: Record<string, BadgeSpec> = {
  OPEN: none,
  SUPPORT_INVESTIGATING: none,
  SA_INVESTIGATING: none,
  WAITING_ON_CUSTOMER: { modifier: "waiting", slaPause: "customer" },
  ASSIGNED: none,
  FIXING: none,
  FIXED: none,
  TESTING: none,
  TESTED: none,
  DEPLOYED: none,
  AWAITING_CUSTOMER_CONFIRMATION: waiting,
  CLOSED: success,
  REJECTED: negative,
  ON_HOLD: { modifier: "hold", slaPause: "internal" },
};

const register: Record<string, BadgeSpec> = { OPEN: none, IN_PROGRESS: none, REVIEWED: none, ON_HOLD: hold, CLOSED: success };

export const BADGE_CATALOGUE: Record<WorkTypeCode, Record<string, BadgeSpec>> = {
  STORY: {
    NOT_STARTED: none,
    IN_PROGRESS: none,
    PENDING_REVIEW: none,
    PENDING_REWORK: none,
    REVIEWED: none,
    UNIT_TESTING: none,
    PASSED: none,
    FAILED: none,
    DEPLOYED: none,
    CLOSED: success,
    CANCELLED: negative,
    ON_HOLD: hold,
    // R39 legacy landing statuses (SA-01 §4.4.2 item 8): 17 + 2 CC Story items sit in Jira's customer
    // Defect-workflow "Tested"/"UAT", not story_v2. No inbound transition; leaves via the global
    // hold/reset/cancel transitions, same as any other in-progress status.
    TESTED: none,
    UAT: none,
  },
  TASK: simplified,
  SUBTASK: simplified,
  EPIC: simplified,
  DEFECT: {
    OPEN: none,
    TO_INVESTIGATE: none,
    INVESTIGATING: none,
    ASSIGNED: none,
    FIXING: none,
    FIXED: none,
    TESTING: none,
    TESTED: none,
    UAT: none,
    PASSED: none,
    DEPLOYED: none,
    CLOSED: success,
    REJECTED: negative,
    ON_HOLD: hold,
  },
  // Batch 19 (GRF-62): a Service request is resolved by the service desk, then closed by the PM. Resolved is done, styled as
  // Issue's Resolved (which a PM also closes); the Incident has no Resolved.
  SERVICE_REQUEST: { ...serviceDesk, RESOLVED: success },
  // Batch 21 (GRF-78): Incident version 7 ends a ticket by Cancel too. Batch 25 (Incident version 9): Cancel Requested waits for
  // the PM's approval, styled as the other waiting statuses; the SLA clock keeps running there (no pause).
  INCIDENT: { ...serviceDesk, CANCELLED: negative, CANCEL_REQUESTED: waiting },
  CR: {
    OPEN: none,
    IMPACT_ASSESSMENT: none,
    AWAITING_CUSTOMER_APPROVAL: waiting,
    APPROVED: none,
    ON_HOLD: hold,
    CLOSED: success,
    REJECTED: negative,
    // R39 legacy landing statuses (SA-01 §4.4.2 item 8): REVIEWED (BMS 116, LCDMS 49) has no inbound
    // transition and leaves via Close or the global Closed. TESTED/UAT are the CC-only customer-workflow
    // landing spots (`cc-cr:*->*(legacy)`); no exit modelled yet, so these are reported as stuck items.
    REVIEWED: none,
    TESTED: none,
    UAT: none,
  },
  ISSUE_ACTION: register,
  // M-Issue-Action-Split (2026-09-19): each type's own status set, not the shared `register` shorthand (ISSUE_ACTION/
  // RISK's OPEN/WIP/REVIEWED/ON_HOLD/CLOSED) — Issue and Action now have their own lifecycles.
  // Revision 44 (2026-09-20): replaced the placeholder shape with the real spec — a linear Open -> WIP ->
  // Resolved/Done, plus Cancelled (from Open/WIP only), both closing into CLOSED (role-gated: PM/PD, see
  // db/seed/workflows/ISSUE.issue.json and ACTION.action.json). No ON_HOLD in this version.
  // Revision 54 (2026-10-01): WIP merged into IN_PROGRESS in STORY, ISSUE_ACTION, ISSUE, ACTION and RISK.
  ISSUE: { OPEN: none, IN_PROGRESS: none, RESOLVED: success, CANCELLED: negative, CLOSED: success },
  ACTION: { OPEN: none, IN_PROGRESS: none, DONE: success, CANCELLED: negative, CLOSED: success },
  RISK: register,
  DEPLOYMENT: {
    OPEN: none,
    AWAITING_MR: waiting,
    MR_PASSED: none,
    ROLLBACK_PASSED: { hint: "Rollback test passed before approval" },
    AWAITING_APPROVAL: waiting,
    DEPLOYMENT_APPROVED: waiting,
    SCHEDULED: none,
    DEPLOYING: { live: true },
    DEPLOYED: waiting,
    CLOSED: success,
    DEPLOY_FAILED: alert,
    FAILED: alert,
    ROLLING_BACK: alert,
    ROLLED_BACK: { hint: "The production rollback is complete" },
    REJECTED: negative,
    ON_HOLD: hold,
    CANCELLED: negative,
    // R39 legacy landing status (SA-01 §4.4.2 item 8): 1 CC Deployment item sits in Jira "Resolved" (a
    // customer-workflow status not in DEPLOYMENT.deployment). No exit modelled; reported as a stuck item.
    // Category is `done` in the catalogue, closer to a completed than a negative outcome, so it takes the
    // same success styling as Closed rather than Rejected/Cancelled's negative styling.
    RESOLVED: success,
  },
};

export function badgeFor(type: WorkTypeCode | undefined, code: string): BadgeSpec {
  return (type && BADGE_CATALOGUE[type]?.[code]) || none;
}

export const CATEGORY_WORDS: Record<StatusCategory, string> = {
  todo: "to do",
  in_progress: "in progress",
  done: "done",
};

const MODIFIER_WORDS: Record<BadgeModifier, string> = {
  success: "",
  negative: "not delivered",
  alert: "needs action now",
  hold: "on hold",
  waiting: "waiting for someone else",
};

/** The words a screen reader hears after the label, e.g. "in progress, SLA paused — waiting on customer". */
export function badgeDescription(category: StatusCategory, spec: BadgeSpec): string {
  const parts = [CATEGORY_WORDS[category]];
  if (spec.modifier && MODIFIER_WORDS[spec.modifier]) parts.push(MODIFIER_WORDS[spec.modifier]);
  if (spec.slaPause === "customer") parts.push("SLA paused — waiting on customer");
  if (spec.slaPause === "internal") parts.push("SLA paused — on hold (internal)");
  if (spec.live) parts.push("live now");
  return parts.join(", ");
}
